import { get, post, put, del } from '../api';

/**
 * @typedef {Object} CompressionPreset
 * @property {string} id - UUID string identifier
 * @property {string} user_id - ID of the user who owns this preset
 * @property {string} name - User-defined name (1-64 characters)
 * @property {Object} options - Compression options object
 * @property {boolean} is_default - Whether this is the default preset
 * @property {string} created_at - ISO timestamp of creation
 * @property {string} updated_at - ISO timestamp of last update
 */

/**
 * Get all presets for the current user
 * @returns {Promise<CompressionPreset[]>} Array of preset objects
 */
export const getPresets = async () => {
  return get('/api/1/presets/');
};

/**
 * Get a specific preset by ID
 * @param {string} presetId - The ID of the preset to retrieve
 * @returns {Promise<CompressionPreset>} The preset object
 * @throws {Error} If preset not found or doesn't belong to user
 */
export const getPreset = async (presetId) => {
  return get(`/api/1/presets/${presetId}/`);
};

/**
 * Create a new preset
 * @param {Object} data - The preset data
 * @param {string} data.name - User-defined name for the preset
 * @param {Object} data.options - Compression options object
 * @param {boolean} [data.is_default=false] - Whether this should be the default preset
 * @returns {Promise<CompressionPreset>} The created preset
 */
export const createPreset = async (data) => {
  return post('/api/1/presets/', data);
};

/**
 * Update an existing preset
 * @param {string} presetId - The ID of the preset to update
 * @param {Object} data - The updated preset data
 * @param {string} data.name - User-defined name for the preset
 * @param {Object} data.options - Compression options object
 * @returns {Promise<CompressionPreset>} The updated preset
 */
export const updatePreset = async (presetId, data) => {
  return put(`/api/1/presets/${presetId}/`, data);
};

/**
 * Delete a preset
 * @param {string} presetId - The ID of the preset to delete
 * @returns {Promise<void>}
 * @throws {Error} If preset not found or doesn't belong to user
 */
export const deletePreset = async (presetId) => {
  return del(`/api/1/presets/${presetId}/`);
};

/**
 * Set a preset as the default
 * @param {string} presetId - The ID of the preset to make default
 * @returns {Promise<CompressionPreset>} The updated preset
 * @throws {Error} If preset not found or doesn't belong to user
 */
export const setDefaultPreset = async (presetId) => {
  return post(`/api/1/presets/${presetId}/set-default/`);
};

/**
 * Remove the default status from a preset
 * @param {string} presetId - The ID of the preset to remove default status from
 * @returns {Promise<CompressionPreset>} The updated preset
 * @throws {Error} If preset not found, doesn't belong to user, or isn't default
 */
export const removeDefaultPreset = async (presetId) => {
  return post(`/api/1/presets/${presetId}/remove-default/`);
}; 